// src/pages/locations/hooks/useRegionsList.ts
import React from "react";

import {
  useCountries,
  useRegions,
  useDeleteRegionMutation,
} from "@/api/features/locations";
import type {
  LocationCountry,
  LocationRegion,
} from "@/api/features/locations/types";

import { useDeleteConfirm } from "./useDeleteConfirm";

const PAGE_SIZE = 10;

export type RegionsFilters = {
  name?: string;
  code?: string;
};

/**
 * List + delete state for the Regions tab.
 * The country is owned by the URL (see useLocationScope) and passed in.
 */
export function useRegionsList(params: { countryIso2: string; search: string }) {
  const countryIso2 = String(params.countryIso2 ?? "").trim().toUpperCase();
  const search = params.search;

  const [filters, setFilters] = React.useState<RegionsFilters>({});
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [countryIso2, search, filters.name, filters.code]);

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });

  const countries = React.useMemo<LocationCountry[]>(
    () => countriesRes?.items ?? [],
    [countriesRes]
  );

  const canList = countryIso2 !== "";

  const {
    data: listRes,
    isLoading,
    isFetching,
  } = useRegions(
    canList
      ? {
          countryIso2,
          page,
          limit: PAGE_SIZE,
          q: search.trim() || undefined,
          name: filters.name?.trim() || undefined,
          code: filters.code?.trim() || undefined,
        }
      : { countryIso2: "" }
  );

  const rows = React.useMemo<LocationRegion[]>(
    () => listRes?.items ?? [],
    [listRes]
  );
  const total = listRes?.total ?? 0;

  const deleteMutation = useDeleteRegionMutation();
  const deleting = deleteMutation.isPending;

  const remove = React.useCallback(
    (row: LocationRegion, done: () => void) => {
      deleteMutation.mutate({ id: row.id }, { onSettled: done });
    },
    [deleteMutation]
  );

  const { confirmDelete, askDelete, cancelDelete, confirmDeleteRow } =
    useDeleteConfirm<LocationRegion>({ isDeleting: deleting, remove });

  return {
    countries,
    countriesLoading,

    filters,
    setFilters,

    page,
    setPage,
    pageSize: PAGE_SIZE,

    rows,
    total,
    isLoading: canList ? isLoading : countriesLoading,
    isFetching,

    deleting,
    confirmDelete,
    askDelete,
    cancelDelete,
    confirmDeleteRow,
  };
}
